import type { DateTime } from 'luxon'

import type { BidType, MarketProgram, Status } from '@/features/bidding/constants'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import type { MarketProgramType } from '@/types/marketProgramType'

export type BidMetaData = {
  id?: number
  portfolio: Portfolio
  deliveryDay: MarketDate
  createdAt: DateTime
  marketProgram: MarketProgram
  status: Status
  activationGroupUuid: string
  bidType: BidType
  activationGroupName?: string
}

/**
 * This type replaces the Bid type, combining offeredBid and acceptedBid into ptus
 */
export type ExtendedBid = BidMetaData & {
  ptus: ExtendedBidPtu[]
}

export type ExtendedBidPtu = {
  ptu: Ptu
  offeredVolume: Volume
  acceptedVolume?: Volume
  ptuChunks?: PtuChunk[]
}

export type PastBid = ExtendedBid & {
  actualControllable: ForecastPtu[]
}

// TODO: Remove 'activationGroupUuid' from the omitting once we have activation groups on forecasts
export type Forecast = Omit<BidMetaData, 'activationGroupUuid' | 'bidType'> & {
  forecastPtus: ForecastPtu[]
}

export type Ptu = {
  start: DateTime
  end: DateTime
}

export type ForecastPtu = {
  ptu: Ptu
  volume: Volume
}

export type BidPtu = ForecastPtu & {
  ptuChunks?: PtuChunk[]
}

export type MarketProgramBiddingName =
  | 'FCRD_DOWN'
  | 'FCRD_UP'
  | 'FCRN'
  | 'FFR_UP'
  | 'MFRR_UP'
  | 'MFRR_DOWN'
  | 'NORDICS_FCRD_DOWN_STATIC'
  | 'NORDICS_FCRD_UP_STATIC'
  | 'NORDICS_FCRD_DOWN_DYNAMIC'
  | 'NORDICS_FCRD_UP_DYNAMIC'
  | 'NORDICS_FCRN'

export type OfferedAndForecastAndSuggestedPtu = {
  ptu: Ptu
  offeredVolume: Volume
  forecastedVolume?: Volume
  suggestedVolume?: Volume
  offeredPtuChunks?: PtuChunk[]
}

// The offeredVolume is the volume that the user can modify. Everything else is used to display data in the grid`
export type PtuForTheOfferVolumeDataGrid = {
  ptu: Ptu
  acceptedCapacityVolume?: Volume
  offeredCapacityVolume?: Volume
  offeredVolume: Volume
  forecastedVolume?: Volume
  suggestedVolume?: Volume
  offeredPtuChunks?: PtuChunk[]
}

export enum BulkFillMode {
  FORECAST_RATIO = 'forecasted_ratio',
  SUGGESTED_RATIO = 'suggested_ratio',
  OFFER_RATIO = 'offer_ratio',
  ACCEPTED_RATIO = 'accepted_ratio',
  ABSOLUTE_VALUE = 'absolute_value',
  OFFERED_CAPACITY_RATIO = 'offered_capacity_ratio',
  ACCEPTED_CAPACITY_RATIO = 'accepted_capacity_ratio',
}

export type SuggestedBid = {
  activationGroupUuid: string
  ptu: Ptu
  volume: Volume
}

export type BidVolumes = {
  ptus: ExtendedBidPtu[]
  forecast?: ForecastPtu[]
  suggestedBid?: SuggestedBid[]
}

export type PtuVolumes = {
  ptu: Ptu
  offeredVolume: Volume
  currentAcceptedVolume?: Volume
  acceptedVolume?: Volume
  forecastedVolume?: Volume
  suggestedVolume?: Volume
  offeredCapacityVolume?: Volume
  acceptedCapacityVolume?: Volume
}

export enum PortfolioState {
  NOT_LIVE = 'NOT_LIVE',
  LIVE = 'LIVE',
  LIVE_WITHOUT_BIDDING = 'LIVE_WITHOUT_BIDDING',
  INACTIVE = 'INACTIVE',
}

export type Portfolio = {
  code: string
  countryCode: string
  priceArea: string
  partnerCode: string
  partnerName: string
  name: string
  partition?: string
  state?: PortfolioState // TODO: Make required, it's already not null in the DB, but still declared as optional in API
}

export type PtuChunk = {
  offeredVolume: Volume
  acceptedVolume?: Volume
  offeredPrice: number
  acceptedPrice?: number
}

export type Volume = { quantity: number; unit: string }

export type BidConflict = {
  activationGroupUuid: string
  conflicts: PtuConflict[]
}

export type PtuConflict = {
  ptu: Ptu
  conflictingActivationGroupUuid: string
  conflictingActivationGroupCode: string
  conflictingMarket: MarketProgramType
}
